import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { AlertCircle, Check, CheckCircle2, X, XCircle } from 'lucide-react';

const NotificationContext = createContext();
const NOTIFICATION_EVENT = 'blupit:notification';

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) throw new Error('useNotification must be used within NotificationProvider');
  return context;
};

const notificationMeta = {
  success: {
    title: 'Acción confirmada',
    icon: CheckCircle2,
    iconColor: 'text-[#20b67a]',
    iconBackground: 'bg-[#e4f8f0]',
    progress: 'bg-[#20b67a]'
  },
  error: {
    title: 'Ocurrió un error',
    icon: XCircle,
    iconColor: 'text-[#f05260]',
    iconBackground: 'bg-[#ffebee]',
    progress: 'bg-[#f05260]'
  },
  info: {
    title: 'Información',
    icon: AlertCircle,
    iconColor: 'text-[#5b43e6]',
    iconBackground: 'bg-[#eeeaff]',
    progress: 'bg-[#5b43e6]'
  }
};

const classifyAlert = (message) => {
  const value = String(message || '').toLowerCase();
  return /(éxito|exitos|correctamente|guardad|cread|actualizad|eliminad|enviad|descargad|completad|✅)/.test(value)
    ? 'success'
    : 'error';
};

if (typeof window !== 'undefined' && !window.__blupitAlertBridgeInstalled) {
  window.__blupitAlertBridgeInstalled = true;
  window.__blupitNotificationQueue = window.__blupitNotificationQueue || [];
  window.__blupitOriginalAlert = window.alert;
  window.alert = (message) => {
    const detail = {
      type: classifyAlert(message),
      description: String(message || '')
    };
    if (window.__blupitNotificationReady) {
      window.dispatchEvent(new CustomEvent(NOTIFICATION_EVENT, { detail }));
    } else {
      window.__blupitNotificationQueue.push(detail);
    }
  };
}

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [confirmation, setConfirmation] = useState(null);
  const confirmationResolver = useRef(null);
  const idSequence = useRef(0);

  const removeNotification = useCallback((id) => {
    setNotifications((current) => current.filter((notification) => notification.id !== id));
  }, []);

  const showNotification = useCallback((message, type = 'info', duration = 5000, options = {}) => {
    const normalizedType = type === 'success' ? 'success' : type === 'error' || type === 'warning' || type === 'destructive' ? 'error' : 'info';
    const payload = typeof message === 'object' && message !== null
      ? message
      : { description: String(message || '') };
    const id = options.id || payload.id || `${Date.now()}-${++idSequence.current}`;
    const notification = {
      id,
      type: normalizedType,
      title: options.title || payload.title || notificationMeta[normalizedType].title,
      description: options.description || payload.description || payload.message || '',
      duration: Number.isFinite(duration) ? duration : 5000
    };

    setNotifications((current) => [...current, notification].slice(-4));
    if (notification.duration > 0) {
      window.setTimeout(() => removeNotification(id), notification.duration);
    }
    return id;
  }, [removeNotification]);

  const success = useCallback((message, duration) => showNotification(message, 'success', duration), [showNotification]);
  const error = useCallback((message, duration) => showNotification(message, 'error', duration), [showNotification]);
  const warning = useCallback((message, duration) => showNotification(message, 'error', duration), [showNotification]);
  const info = useCallback((message, duration) => showNotification(message, 'info', duration), [showNotification]);

  const confirmAction = useCallback((messageOrOptions) => {
    const options = typeof messageOrOptions === 'object' && messageOrOptions !== null
      ? messageOrOptions
      : { description: String(messageOrOptions || 'Los cambios se aplicarán de inmediato.') };

    if (confirmationResolver.current) confirmationResolver.current(false);
    return new Promise((resolve) => {
      confirmationResolver.current = resolve;
      setConfirmation({
        title: options.title || '¿Confirmar acción?',
        description: options.description || options.message || 'Los cambios se aplicarán de inmediato.',
        confirmText: options.confirmText || 'Confirmar',
        cancelText: options.cancelText || 'Cancelar'
      });
    });
  }, []);

  const resolveConfirmation = useCallback((accepted) => {
    confirmationResolver.current?.(accepted);
    confirmationResolver.current = null;
    setConfirmation(null);
  }, []);

  useEffect(() => {
    const handleGlobalNotification = (event) => {
      const detail = event.detail || {};
      if (detail.action === 'dismiss') {
        if (detail.id) removeNotification(detail.id);
        else setNotifications([]);
        return;
      }
      showNotification(
        { id: detail.id, title: detail.title, description: detail.description || detail.message },
        detail.type || detail.variant || 'info',
        detail.duration ?? 5000
      );
    };
    window.addEventListener(NOTIFICATION_EVENT, handleGlobalNotification);
    window.__blupitNotificationReady = true;
    const pendingNotifications = window.__blupitNotificationQueue.splice(0);
    pendingNotifications.forEach((detail) => handleGlobalNotification({ detail }));
    window.appConfirm = confirmAction;

    return () => {
      window.removeEventListener(NOTIFICATION_EVENT, handleGlobalNotification);
      window.__blupitNotificationReady = false;
      if (window.appConfirm === confirmAction) delete window.appConfirm;
      if (confirmationResolver.current) confirmationResolver.current(false);
    };
  }, [confirmAction, removeNotification, showNotification]);

  return (
    <NotificationContext.Provider value={{ success, error, warning, info, showNotification, confirm: confirmAction }}>
      {children}
      <NotificationContainer
        confirmation={confirmation}
        notifications={notifications}
        onConfirm={resolveConfirmation}
        onRemove={removeNotification}
      />
    </NotificationContext.Provider>
  );
};

const NotificationContainer = ({ confirmation, notifications, onConfirm, onRemove }) => (
  <div
    className="pointer-events-none fixed right-4 top-4 z-[300] flex w-[calc(100vw-2rem)] max-w-[440px] flex-col gap-3 sm:right-6 sm:top-6"
    aria-live="polite"
    aria-label="Notificaciones"
  >
    {confirmation && (
      <ConfirmationNotification confirmation={confirmation} onResolve={onConfirm} />
    )}
    {notifications.map((notification) => (
      <StatusNotification
        key={notification.id}
        notification={notification}
        onClose={() => onRemove(notification.id)}
      />
    ))}
  </div>
);

const CardShell = ({ children, onClose }) => (
  <section className="pointer-events-auto overflow-hidden rounded-[22px] border border-[#e1e5ed] bg-white shadow-[0_18px_50px_rgba(23,32,51,.16)] animate-in slide-in-from-right-5 fade-in duration-200">
    <div className="relative p-5 sm:p-6">
      {children}
      <button
        type="button"
        onClick={onClose}
        className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-xl bg-[#f7f8fa] text-[#7a8397] transition hover:bg-[#efecff] hover:text-[#5b43e6] focus:outline-none focus:ring-2 focus:ring-[#d8d0ff]"
        aria-label="Cerrar notificación"
      >
        <X size={20} strokeWidth={1.8} />
      </button>
    </div>
  </section>
);

const ConfirmationNotification = ({ confirmation, onResolve }) => (
  <CardShell onClose={() => onResolve(false)}>
    <div className="flex items-start gap-4 pr-10">
      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[15px] bg-[#eeeaff] text-[#5b43e6]">
        <AlertCircle size={27} strokeWidth={1.8} />
      </span>
      <div className="min-w-0 pt-0.5">
        <h2 className="text-[18px] font-normal leading-6 text-[#172033]">{confirmation.title}</h2>
        <p className="mt-1 whitespace-pre-line text-[14px] font-light leading-5 text-[#758096]">{confirmation.description}</p>
      </div>
    </div>
    <div className="mt-5 flex justify-end gap-3 border-t border-[#edf0f4] pt-5">
      <button
        type="button"
        onClick={() => onResolve(false)}
        className="h-11 rounded-[14px] border border-[#dfe4ed] bg-white px-6 text-[13px] font-normal text-[#3b455b] transition hover:border-[#cfc7ff] hover:text-[#5b43e6] focus:outline-none focus:ring-2 focus:ring-[#e9e5ff]"
      >
        {confirmation.cancelText}
      </button>
      <button
        type="button"
        onClick={() => onResolve(true)}
        className="flex h-11 items-center justify-center gap-2 rounded-[14px] bg-gradient-to-br from-[#6d5dfb] to-[#4b2fdb] px-6 text-[13px] font-normal text-white shadow-[0_7px_16px_rgba(91,67,230,.2)] transition hover:brightness-105 focus:outline-none focus:ring-2 focus:ring-[#cfc7ff]"
      >
        <Check size={18} />
        {confirmation.confirmText}
      </button>
    </div>
  </CardShell>
);

const StatusNotification = ({ notification, onClose }) => {
  const meta = notificationMeta[notification.type] || notificationMeta.info;
  const Icon = meta.icon;
  return (
    <CardShell onClose={onClose}>
      <div className="flex items-start gap-4 pr-10">
        <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-[15px] ${meta.iconBackground} ${meta.iconColor}`}>
          <Icon size={27} strokeWidth={1.8} />
        </span>
        <div className="min-w-0 pt-0.5">
          <h2 className="text-[18px] font-normal leading-6 text-[#172033]">{notification.title}</h2>
          {notification.description && (
            <p className="mt-1 whitespace-pre-line text-[14px] font-light leading-5 text-[#758096]">{notification.description}</p>
          )}
        </div>
      </div>
      {notification.duration > 0 && (
        <span
          className={`absolute bottom-0 left-5 right-5 h-1 origin-left rounded-full ${meta.progress}`}
          style={{ animation: `notification-progress ${notification.duration}ms linear forwards` }}
        />
      )}
    </CardShell>
  );
};

export const dispatchGlobalNotification = (detail) => {
  if (typeof window === 'undefined') return null;
  window.dispatchEvent(new CustomEvent(NOTIFICATION_EVENT, { detail }));
  return detail?.id || null;
};
